import type { LicenseEntitlementBooleanKey, LicenseEntitlementNumericKey, LicenseEntitlements, LicenseLimit, LicenseLimitGate, LicenseLimitStatus, LicenseManagement, LicenseScope, LicenseUsage, WorkspaceUsageEntry } from '#layers/license/types/license';
import { defineStore, storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { createRequestTracker, singleFlight, useSystemFetch, useSystemInfo } from '#layers/base/public';
import { LicenseSchema } from '#layers/license/schema/license';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { useWorkspaceStore } from '#layers/workspace/public';

const LICENSE_MAX_AGE_MS = 2000;

/** License data scoped to the active workspace, falling back to system scope. */
export const useLicenseStore = defineStore('license', () => licenseStoreSetup());

/** License data pinned to system scope for org-level surfaces: org entitlements live only in the `system/license` payload, not the active workspace's. */
export const useSystemLicenseStore = defineStore('license:system', () => licenseStoreSetup('system'));

function licenseStoreSetup(pinnedScope?: LicenseScope) {
	const systemInfo = useSystemInfo();
	const { currentWorkspaceSlug } = storeToRefs(useWorkspaceStore());
	const systemFetch = useSystemFetch();

	const entitlements = ref<LicenseEntitlements>({});
	const usage = ref<LicenseUsage>({});
	const management = ref<LicenseManagement | null>(null);
	const isLoaded = ref(false);
	const isLoading = ref(false);
	const loadError = ref<unknown | null>(null);
	const loadedAt = ref(0);
	// Auth-agnostic: nothing loads until `setEnabled(true)`
	const enabled = ref(false);

	const requests = createRequestTracker();

	const scope = computed<LicenseScope>(() => pinnedScope ?? (currentWorkspaceSlug.value ? 'workspace' : 'system'));

	const scopeKey = computed(() => {
		if (!enabled.value) return null;
		return scope.value === 'workspace' ? `workspace:${currentWorkspaceSlug.value}` : 'system';
	});

	const canRevalidate = computed(() => management.value?.source === 'env-key');

	let load = singleFlight(runLoad);

	watch(scopeKey, (key, previous) => {
		if (key === previous) return;
		reset();
		if (key) ensureLoaded();
	}, { immediate: true });

	async function runLoad() {
		const request = requests.start();
		isLoading.value = true;
		loadError.value = null;

		try {
			// The store outlives runtimes, so the workspace transport is captured per load, not at setup.
			const raw = scope.value === 'system'
				? await systemFetch('system/license')
				: await useWorkspaceRuntimeHandle().fetch('license');
			const parsed = LicenseSchema.parse(raw);

			if (!request.isCurrent()) return;

			entitlements.value = parsed.entitlements;
			usage.value = parsed.usage;
			management.value = parsed.management ?? null;
			isLoaded.value = true; // set only on success
			loadedAt.value = Date.now();
		}
		catch (error) {
			if (request.isCurrent()) {
				loadError.value = error;
				isLoaded.value = false;
			}
		}
		finally {
			if (request.isCurrent()) {
				isLoading.value = false;
			}
		}
	}

	async function ensureLoaded() {
		if (!enabled.value) return;
		if (isLoaded.value) return;
		await load();
	}

	async function refresh() {
		if (!enabled.value) return;
		await load();
	}

	async function ensureFresh(maxAgeMs = LICENSE_MAX_AGE_MS) {
		if (!enabled.value) return;
		if (isLoaded.value && Date.now() - loadedAt.value <= maxAgeMs) return;
		await load();
	}

	async function revalidate() {
		if (!canRevalidate.value) return;

		await systemFetch('system/license/refresh', { method: 'POST' });

		await load.settled();
		await load();
		// Re-read `/system/info` so the derived `status` reflects the revalidated license.
		await systemInfo.refresh();
	}

	function reset() {
		entitlements.value = {};
		usage.value = {};
		management.value = null;
		isLoaded.value = false;
		isLoading.value = false;
		loadError.value = null;
		loadedAt.value = 0;
		requests.invalidate();
		load = singleFlight(runLoad);
	}

	function setEnabled(value: boolean) {
		enabled.value = value;
	}

	function hasEntitlement(key: LicenseEntitlementBooleanKey): boolean {
		const entitlement = entitlements.value[key];
		if (!entitlement) return false;
		return entitlement.override ?? entitlement.default;
	}

	function getEntitlementLimit(key: LicenseEntitlementNumericKey): LicenseLimit {
		const entitlement = entitlements.value[key];

		if (!entitlement) {
			return { limit: 0, allowance: 0, usage: 0, effective: 0, remaining: 0, gate: 'hard', isOverAllowance: false, status: 'block', perWorkspace: null };
		}

		const reportedUsage = usage.value[key] ?? null;
		const { limit, overage, addon } = entitlement;

		const gate = getLimitGate();
		// `-1` = unlimited. `overage` is billed headroom above the allowance, so it only gates `effective`.
		const allowance = (limit === -1 || addon === -1) ? null : limit + (addon ?? 0);
		const effective = (allowance === null || overage === -1) ? null : allowance + (overage ?? 0);

		if (typeof reportedUsage === 'object' && reportedUsage !== null) {
			const perWorkspace: WorkspaceUsageEntry[] = Object.entries(reportedUsage).map(([workspaceId, workspaceUsage]) => ({
				workspaceId,
				usage: workspaceUsage,
				isOverAllowance: isOverAllowance(workspaceUsage),
				status: getLimitStatus(workspaceUsage),
			}));

			return {
				limit,
				allowance,
				usage: null,
				effective: null,
				remaining: null,
				gate,
				isOverAllowance: perWorkspace.some((entry) => entry.isOverAllowance),
				status: null,
				perWorkspace,
			};
		}

		const remaining = (reportedUsage === null || effective === null) ? null : effective - reportedUsage;

		return {
			limit,
			allowance,
			usage: reportedUsage,
			effective,
			remaining,
			gate,
			isOverAllowance: isOverAllowance(reportedUsage),
			status: getLimitStatus(reportedUsage),
			perWorkspace: null,
		};

		function isOverAllowance(value: number | null): boolean {
			return allowance !== null && value !== null && value > allowance;
		}

		function getLimitGate(): LicenseLimitGate {
			if (limit === -1 || addon === -1) return 'none';
			if (overage === -1) return 'soft';
			return 'hard';
		}

		function getLimitStatus(value: number | null): LicenseLimitStatus {
			if (gate === 'none' || value === null) return 'allow';
			if (gate === 'soft') return allowance !== null && value >= allowance ? 'warn' : 'allow';
			return effective !== null && value >= effective ? 'block' : 'allow';
		}
	}

	return {
		status: computed(() => systemInfo.license.status),
		expiresAt: computed(() => systemInfo.license.expiresAt),
		renewsAt: computed(() => systemInfo.license.renewsAt),
		gracePeriod: computed(() => systemInfo.license.gracePeriod),
		management: computed(() => management.value),
		isLoaded: computed(() => isLoaded.value),
		isLoading: computed(() => isLoading.value),
		loadError: computed(() => loadError.value),
		canRevalidate,
		setEnabled,
		hasEntitlement,
		getEntitlementLimit,
		ensureLoaded,
		refresh,
		ensureFresh,
		revalidate,
	};
}
